/**
 * Mirrors the backend rules for variation values (VariationDefinition
 * .normalizeValue), so a value is refused on screen for the same reasons the
 * API would refuse it.
 */

export type VariationValueType = "string" | "number";

export interface VariationValueRules {
  type: VariationValueType;
  decimalPlaces?: number | null;
}

export const MAX_VARIATION_DECIMAL_PLACES = 3;

/** Snake-case key of a text value: "Azul Marinho" is "azul_marinho". */
export function toVariationValueKey(label: string): string {
  return label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}

interface VariationNumber {
  integer: string;
  fraction: string;
}

/** Reads "10", "10,5" or "10.5". No signs, no thousands separators. */
function parseVariationNumber(input: string): VariationNumber | null {
  const match = /^(\d+)(?:[.,](\d+))?$/.exec(input.trim());
  if (!match) return null;
  return {
    integer: match[1].replace(/^0+(?=\d)/, ""),
    fraction: (match[2] ?? "").replace(/0+$/, ""),
  };
}

/** Reads a numeric key ("10_5") back into a number. */
function parseNumericValueKey(key: string): VariationNumber | null {
  const match = /^(\d+)(?:_(\d+))?$/.exec(key);
  if (!match) return null;
  return { integer: match[1], fraction: match[2] ?? "" };
}

function formatNumericLabel(
  number: VariationNumber,
  decimalPlaces: number,
): string {
  return decimalPlaces > 0
    ? `${number.integer},${number.fraction.padEnd(decimalPlaces, "0")}`
    : number.integer;
}

export type NormalizedVariationValue =
  | { ok: true; value: string; label: string }
  | { ok: false; error: string };

/**
 * Key and label a typed value is saved with. Numbers are keyed by the number
 * ("10_5") and labelled with the variation's decimal places ("10,50").
 */
export function normalizeVariationValue(
  rules: VariationValueRules,
  typed: string,
): NormalizedVariationValue {
  const trimmed = typed.trim();
  if (rules.type !== "number") {
    const value = toVariationValueKey(trimmed);
    return value
      ? { ok: true, value, label: trimmed }
      : { ok: false, error: "Informe um valor válido." };
  }

  const number = parseVariationNumber(trimmed);
  if (!number) {
    return { ok: false, error: "Informe um número válido, como 10 ou 10,5." };
  }

  const decimalPlaces = rules.decimalPlaces ?? 0;
  if (number.fraction.length > decimalPlaces) {
    return {
      ok: false,
      error:
        decimalPlaces === 0
          ? "Esta variação não aceita casas decimais."
          : `Esta variação aceita no máximo ${decimalPlaces} casa(s) decimal(is).`,
    };
  }

  return {
    ok: true,
    value: number.fraction
      ? `${number.integer}_${number.fraction}`
      : number.integer,
    label: formatNumericLabel(number, decimalPlaces),
  };
}

/** Relabels a saved numeric value for other decimal places, when it fits. */
export function relabelNumericValue(
  key: string,
  decimalPlaces: number,
): string | null {
  const number = parseNumericValueKey(key);
  if (!number || number.fraction.length > decimalPlaces) return null;
  return formatNumericLabel(number, decimalPlaces);
}

/** Keeps digits and a single comma while a number is typed. */
export function sanitizeNumberInput(text: string): string {
  const cleaned = text.replace(/\./g, ",").replace(/[^\d,]/g, "");
  const [integer, ...rest] = cleaned.split(",");
  return rest.length > 0 ? `${integer},${rest.join("")}` : integer;
}

/** "10,50 cm" — the value as shown to store owners and customers. */
export function formatVariationValue(
  label: string,
  unit?: string | null,
): string {
  return unit ? `${label} ${unit}` : label;
}
